import { FormEvent, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Camera, ChevronDown, ChevronUp, Loader2, Plus, Trash2, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '../services/api'

async function compressImage(file: Blob): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file)
    const max = 1280
    const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(bitmap.width * scale))
    canvas.height = Math.max(1, Math.round(bitmap.height * scale))
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    const out: Blob | null = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.82)
    })
    return out || file
  } catch {
    return file
  }
}

function macrosFromItem(it: any, grams: number) {
  const g = Math.max(10, grams)
  if (it.caloriesPer100g != null) {
    const f = g / 100
    const r = (n: number) => Math.round(n * 10) / 10
    return {
      grams: g,
      calories: r(Number(it.caloriesPer100g) * f),
      protein: r(Number(it.proteinPer100g || 0) * f),
      carbs: r(Number(it.carbsPer100g || 0) * f),
      fat: r(Number(it.fatPer100g || 0) * f),
    }
  }
  return { grams: g, calories: Number(it.calories) || 0, protein: Number(it.protein) || 0, carbs: Number(it.carbs) || 0, fat: Number(it.fat) || 0 }
}

export default function PhotoFoodLog({
  mealType,
  customMealName,
  onRegistered,
}: {
  mealType: string
  customMealName?: string
  onRegistered: () => void
}) {
  const [open, setOpen] = useState(false)
  const [camera, setCamera] = useState(false)
  const [busy, setBusy] = useState(false)
  const [saving, setSaving] = useState(false)
  const [preview, setPreview] = useState('')
  const [message, setMessage] = useState('')
  const [simulated, setSimulated] = useState(false)
  const [items, setItems] = useState<any[]>([])
  const [addQ, setAddQ] = useState('')
  const [addHits, setAddHits] = useState<any[]>([])
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const capturingRef = useRef(false)

  const stopCam = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
  }

  useEffect(() => () => {
    stopCam()
    if (preview) URL.revokeObjectURL(preview)
  }, [])

  useEffect(() => {
    if (!camera) return
    let cancelled = false
    ;(async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
        })
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current = stream
        if (!videoRef.current) {
          await new Promise((r) => requestAnimationFrame(() => r(null)))
        }
        const video = videoRef.current
        if (video) {
          video.srcObject = stream
          video.muted = true
          video.playsInline = true
          video.controls = false
          video.setAttribute('playsinline', 'true')
          video.setAttribute('webkit-playsinline', 'true')
          const kick = () => { void video.play().catch(() => {}) }
          if (video.readyState >= 1) kick()
          else video.onloadedmetadata = kick
          kick()
        }
      } catch {
        toast.error('No se pudo abrir la cámara. Usa la galería.')
        setCamera(false)
      }
    })()
    return () => {
      cancelled = true
      stopCam()
    }
  }, [camera])

  const analyzeBlob = async (blob: Blob) => {
    setBusy(true)
    try {
      const compact = await compressImage(blob)
      const fd = new FormData()
      fd.append('photo', compact, 'plato.jpg')
      const { data } = await api.post('/nutrition/photo-log/analyze', fd)
      setItems(Array.isArray(data?.items) ? data.items : [])
      setSimulated(Boolean(data?.simulated))
      setMessage(String(data?.message || ''))
      if (!(data?.items || []).length) toast.error('No se vieron alimentos. Prueba más luz o agrégalos a mano.')
      else toast.success('Revisa y confirma las cantidades')
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'No se pudo analizar la foto'
      toast.error(Array.isArray(msg) ? msg[0] : String(msg))
    } finally {
      setBusy(false)
    }
  }

  const onFile = async (file?: File) => {
    if (!file) return
    if (preview) URL.revokeObjectURL(preview)
    setPreview(URL.createObjectURL(file))
    setItems([])
    await analyzeBlob(file)
  }

  const capture = async () => {
    if (busy || capturingRef.current) return
    const video = videoRef.current
    if (!video) return
    capturingRef.current = true
    try {
      if (video.paused) await video.play().catch(() => {})
      if (!video.videoWidth) {
        await new Promise((r) => setTimeout(r, 350))
      }
      if (!video.videoWidth || !video.videoHeight) {
        toast.error('La cámara aún no está lista. Toca Capturar de nuevo o usa Galería.')
        return
      }
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      canvas.getContext('2d')?.drawImage(video, 0, 0)
      const blob: Blob | null = await new Promise((res) => canvas.toBlob((b) => res(b), 'image/jpeg', 0.85))
      if (!blob) {
        toast.error('No se pudo capturar. Prueba Galería.')
        return
      }
      stopCam()
      setCamera(false)
      if (preview) URL.revokeObjectURL(preview)
      setPreview(URL.createObjectURL(blob))
      setItems([])
      await analyzeBlob(blob)
    } finally {
      capturingRef.current = false
    }
  }

  const patchGrams = (idx: number, grams: number) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...macrosFromItem(it, grams) } : it)))
  }

  const searchAdd = async (e: FormEvent) => {
    e.preventDefault()
    const q = addQ.trim()
    if (q.length < 2) return
    try {
      const { data } = await api.get('/nutrition/foods/search', { params: { q, limit: 8 } })
      setAddHits(Array.isArray(data) ? data : [])
    } catch {
      setAddHits([])
    }
  }

  const addFood = (f: any) => {
    const row = macrosFromItem({
      ...f,
      caloriesPer100g: f.caloriesPer100g ?? f.calories_per_100g,
      proteinPer100g: f.proteinPer100g ?? f.protein_per_100g,
      carbsPer100g: f.carbsPer100g ?? f.carbs_per_100g,
      fatPer100g: f.fatPer100g ?? f.fat_per_100g,
    }, 100)
    setItems((prev) => [...prev, {
      visionName: f.name,
      name: f.name,
      foodItemId: f.id,
      source: 'catalog',
      caloriesPer100g: f.caloriesPer100g ?? f.calories_per_100g,
      proteinPer100g: f.proteinPer100g ?? f.protein_per_100g,
      carbsPer100g: f.carbsPer100g ?? f.carbs_per_100g,
      fatPer100g: f.fatPer100g ?? f.fat_per_100g,
      ...row,
    }])
    setAddQ('')
    setAddHits([])
  }

  const confirm = async () => {
    const payloadItems = items.filter((it) => {
      if (Number(it.grams) < 10) return false
      if (it.foodItemId) return true
      return Number(it.calories || 0) || Number(it.protein || 0) || Number(it.carbs || 0) || Number(it.fat || 0)
    })
    if (!payloadItems.length) {
      toast.error('Ajusta al menos un alimento')
      return
    }
    setSaving(true)
    try {
      await api.post('/nutrition/photo-log/confirm', {
        mealType,
        customMealName: customMealName || undefined,
        items: payloadItems.map((it) => ({
          foodItemId: it.foodItemId || null,
          name: it.name,
          visionName: it.visionName || it.name,
          grams: it.grams,
          calories: it.calories,
          protein: it.protein,
          carbs: it.carbs,
          fat: it.fat,
        })),
      })
      toast.success('Comida registrada. El próximo escaneo usará estas cantidades.')
      setItems([])
      if (preview) URL.revokeObjectURL(preview)
      setPreview('')
      setMessage('')
      onRegistered()
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'No se pudo registrar'
      toast.error(Array.isArray(msg) ? msg[0] : String(msg))
    } finally {
      setSaving(false)
    }
  }

  const totals = items.reduce((a, it) => ({
    calories: a.calories + Number(it.calories || 0),
    protein: a.protein + Number(it.protein || 0),
    carbs: a.carbs + Number(it.carbs || 0),
    fat: a.fat + Number(it.fat || 0),
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 })

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white mb-2">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
        <span className="font-medium text-gray-700 flex items-center gap-2 text-sm">
          <Camera size={16} className="text-[#64ba30]" /> Registro fotográfico
        </span>
        {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>
      {open && (
        <div className="border-t border-gray-100 p-3 space-y-3">
          <p className="text-xs text-gray-500">Toma una foto del plato. La IA nombra los alimentos; tú confirmas los gramos. Cada registro enseña al siguiente.</p>
          <div className="flex gap-2">
            <button type="button" onClick={() => setCamera(true)}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white bg-[#64ba30] hover:bg-[#58a529]">
              Abrir cámara
            </button>
            <button type="button" onClick={() => fileRef.current?.click()}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium text-[#64ba30] border-2 border-green-400 hover:bg-green-50">
              Galería
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = '' }} />
          </div>
          {preview && (
            <img src={preview} alt="Plato" className="w-full max-h-52 object-cover rounded-xl border border-gray-100" />
          )}
          {busy && (
            <p className="text-sm text-gray-500 flex items-center gap-2 justify-center py-2">
              <Loader2 size={16} className="animate-spin" /> Identificando alimentos…
            </p>
          )}
          {simulated && items.length > 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">{message}</p>
          )}
          {!simulated && message && items.length > 0 && (
            <p className="text-xs text-gray-500">{message}</p>
          )}
          {items.length > 0 && (
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={`${it.name}-${i}`} className="p-2.5 bg-gray-50 rounded-xl">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{it.name}</p>
                      <p className="text-xs text-gray-500">
                        {Math.round(it.calories)} kcal · {it.protein} g prot
                        {it.missingCatalog ? ' · sin catálogo' : ''}
                        {it.source === 'memory' ? ' · tu historial' : ''}
                      </p>
                    </div>
                    <button type="button" className="text-red-400 p-1" onClick={() => setItems((p) => p.filter((_, j) => j !== i))} aria-label="Quitar">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <button type="button" className="w-7 h-7 rounded-full bg-white border border-gray-200 font-bold text-sm"
                      onClick={() => patchGrams(i, Math.max(20, Number(it.grams) - 10))}>−</button>
                    <input
                      type="number"
                      min={20}
                      max={600}
                      value={it.grams}
                      onChange={(e) => patchGrams(i, Number(e.target.value) || 20)}
                      className="w-16 text-center text-sm rounded-lg border border-gray-200 py-1 text-gray-800"
                      aria-label={`Gramos de ${it.name}`}
                    />
                    <span className="text-xs text-gray-400">g</span>
                    <button type="button" className="w-7 h-7 rounded-full bg-white border border-gray-200 font-bold text-sm"
                      onClick={() => patchGrams(i, Math.min(600, Number(it.grams) + 10))}>+</button>
                    {[50, 100, 150].map((g) => (
                      <button key={g} type="button" onClick={() => patchGrams(i, g)}
                        className="text-[11px] px-2 py-1 rounded-lg bg-white border border-gray-200 text-gray-600">{g}</button>
                    ))}
                  </div>
                </div>
              ))}
              <form onSubmit={searchAdd} className="flex gap-2">
                <input value={addQ} onChange={(e) => setAddQ(e.target.value)} placeholder="Agregar alimento del catálogo"
                  className="flex-1 input text-sm" />
                <button type="submit" className="btn-secondary text-xs">Buscar</button>
              </form>
              {addHits.length > 0 && (
                <div className="border border-gray-200 rounded-xl divide-y max-h-36 overflow-y-auto">
                  {addHits.map((f) => (
                    <button key={f.id} type="button" onClick={() => addFood(f)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50">{f.name}</button>
                  ))}
                </div>
              )}
              <div className="bg-green-50 rounded-xl p-3 text-center">
                <p className="text-sm font-bold text-green-700">
                  Total: {Math.round(totals.calories)} kcal · {Math.round(totals.protein)} g prot · {Math.round(totals.carbs)} g CHO
                </p>
              </div>
              <button type="button" disabled={saving} onClick={() => void confirm()}
                className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-[#64ba30] hover:bg-[#58a529] disabled:opacity-50 flex items-center justify-center gap-2">
                <Plus size={16} /> {saving ? 'Registrando…' : 'Registrar en esta comida'}
              </button>
            </div>
          )}
        </div>
      )}

      {camera && createPortal(
        <div className="food-plan-root food-portal-modal photo-food-camera fixed inset-0 z-[200] bg-black flex flex-col h-[100dvh]">
          <div className="flex items-center justify-between p-4 bg-black/80" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
            <span className="text-white font-semibold flex items-center gap-2"><Camera size={18} /> Foto del plato</span>
            <button type="button" className="text-white p-2" onClick={() => { stopCam(); setCamera(false) }} aria-label="Cerrar">
              <X size={22} />
            </button>
          </div>
          <div
            role="button"
            tabIndex={0}
            className="flex-1 relative bg-black"
            onClick={() => void capture()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); void capture() } }}
            aria-label="Capturar foto"
          >
            <video
              ref={videoRef}
              className="w-full h-full object-cover pointer-events-none"
              playsInline
              muted
              autoPlay
              disablePictureInPicture
              controls={false}
            />
            <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/90 text-sm bg-black/45 rounded-full px-3 py-1 pointer-events-none">
              Toca la pantalla para capturar
            </span>
          </div>
          <div className="p-4 bg-black/90 space-y-2" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
            <button
              type="button"
              disabled={busy}
              onClick={() => void capture()}
              className="w-full py-3.5 rounded-full bg-[#64ba30] text-white text-base font-semibold disabled:opacity-60"
            >
              {busy ? 'Identificando…' : 'Capturar'}
            </button>
            <p className="text-white/60 text-[11px] text-center">Encuadra el plato con buena luz. Luego confirmas gramos.</p>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
